import { z } from 'zod';

import { getPhoneDigits, getPhoneNumberLengthIssue, isValidSportsFiPhoneNumber } from '@/lib/utils/phone-number';

export const requiredNumber = (
  fieldName: string,
  min?: number,
  max?: number,
  messages?: { min?: string; max?: string }
) => {
  let schema = z.number({
    required_error: `${fieldName} is required`,
  });

  if (min !== undefined) {
    schema = schema.min(min, {
      message: messages?.min || `${fieldName} must be at least ${min}`,
    });
  }

  if (max !== undefined) {
    schema = schema.max(max, {
      message: messages?.max || `${fieldName} cannot exceed ${max}`,
    });
  }

  return schema;
};

export const requiredString = (fieldName: string, minLength: number) =>
  z.string({ required_error: `${fieldName} is required` }).min(minLength, {
    message: `${fieldName} must be at least ${minLength} characters`,
  });

export const requiredPhoneNumber = () => {
  return z
    .string()
    .trim()
    .superRefine((phoneNumber, ctx) => {
      const digits = getPhoneDigits(phoneNumber);

      if (digits.length < 7) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Phone number is too short (minimum 7 digits)',
        });

        return;
      }

      if (digits.length > 15) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Phone number is too long (maximum 15 digits)',
        });

        return;
      }

      const lengthIssue = getPhoneNumberLengthIssue(phoneNumber);

      if (lengthIssue === 'TOO_SHORT') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Phone number is too short (minimum 7 digits)',
        });

        return;
      }

      if (lengthIssue === 'TOO_LONG') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Phone number is too long (maximum 15 digits)',
        });

        return;
      }

      if (lengthIssue === 'INVALID_LENGTH') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid phone number length for selected country',
        });

        return;
      }

      if (!isValidSportsFiPhoneNumber(phoneNumber)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid phone number format',
        });
      }
    });
};

export const requiredDate = (fieldName: string) =>
  z.preprocess(
    (date) => (typeof date === 'string' ? new Date(date) : date),
    z.date({
      required_error: `${fieldName} is required.`,
      invalid_type_error: `${fieldName} must be a valid date.`,
    })
  );

export const requiredBoolean = (fieldName: string) =>
  z.boolean({
    required_error: `${fieldName} is required.`,
    invalid_type_error: `${fieldName} must be a boolean.`,
  });

export const requiredStringArray = (fieldName: string, minLength: number) =>
  z
    .array(z.string(), { required_error: `${fieldName} is required.` })
    .min(minLength, { message: `At least ${minLength} ${fieldName} required.` });

export const optionalString = () => z.string().optional();
